import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { buttonVariants } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { loginPathFor, safeNext } from '@/lib/next-url';
import { cn } from '@/lib/utils';
import { useAuthStore, type Role } from '@/stores/auth.store';

/**
 * Route guards (05 §1.2).
 *
 * The `unknown` status matters: it is the window between page load and the
 * silent refresh resolving. Redirecting during it would sign a valid session out
 * on every reload, so the guard renders a spinner instead.
 */

export interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { t } = useTranslation();
  const status = useAuthStore((state) => state.status);
  const location = useLocation();

  if (status === 'unknown') {
    return (
      <div
        className="grid min-h-[60vh] place-items-center"
        data-testid="auth-bootstrapping"
        aria-busy="true"
      >
        <Spinner label={t('common.loading')} />
      </div>
    );
  }

  if (status === 'anonymous') {
    return <Navigate to={loginPathFor(location.pathname, location.search)} replace />;
  }

  return <>{children}</>;
}

export interface RequireRoleProps {
  roles: readonly Role[];
  children: ReactNode;
}

/**
 * Role gate. It renders an honest "this screen is for admins" page rather than
 * redirecting: a redirect from `/admin/halls` to `/` looks like a broken link to
 * a TA who followed a colleague's URL, and a redirect back into a guarded route
 * is how redirect loops start.
 */
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { t } = useTranslation();
  const actor = useAuthStore((state) => state.actor);

  if (!actor || !roles.includes(actor.role)) {
    return (
      <div className="mx-auto max-w-xl space-y-4 py-10" data-testid="admin-only">
        <Alert variant="warning" title={t('auth.adminOnly.title')}>
          {t('auth.adminOnly.description')}
        </Alert>
        <Link to="/" className={cn(buttonVariants({ variant: 'outline' }))}>
          {t('common.backToHome')}
        </Link>
      </div>
    );
  }

  return <>{children}</>;
}

/** `/admin/*` — ADMIN only, on top of `RequireAuth`. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  return <RequireRole roles={['ADMIN']}>{children}</RequireRole>;
}

/** Redirects an already-signed-in user away from `/login`. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const location = useLocation();

  if (status === 'authenticated') {
    const next = new URLSearchParams(location.search).get('next');
    return <Navigate to={safeNext(next)} replace />;
  }

  return <>{children}</>;
}
